import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

import { useUserStore } from '@/stores/userStore'
import useLogger from '@/services/loggerService'

import useBlProviders from '@/composables/providers/useBlProvider'
import NotificationsDt from '@/services/dataTransferModules/notificationsDt'

const notificationsDt = new NotificationsDt()

export default function useNotifications() {
	const { t: $t } = useI18n()
	const userStore = useUserStore()
	const { selectedCompanyId } = storeToRefs(userStore)
	const logger = useLogger()

	const {
		loadEntityFlags: flags,
		getPreparedQueryForDt,

		entities: notifications,
		getEntities: getNotifications,

		entity: notification,
		getEntity: getNotification,

		entityPreview: notificationPreview,
		getEntityPreview: getNotificationPreview,

		patchEntity: updateNotifications,
	} = useBlProviders(notificationsDt, {
		showErrorNotificationForLoadingAllEntities: true,
		errorNotificationTextForLoadingAllEntities: $t('Error loading notifications'),

		showErrorNotificationForLoadingOneEntity: true,
		errorNotificationTextForLoadingOneEntity: $t('Error loading notification'),

		showErrorNotificationForUpdatingEntity: true,
		errorNotificationTextForUpdatingEntity: $t('Error updating notification'),
	})

	const notificationsUnreadCount = ref({})

	/**
	 * Отдельный кешируемый эндпоинт (.../notifications/unread_count/), провайдер его не покрывает.
	 */
	const getNotificationsUnreadCount = (payload = {}) => {
		const preparedQuery = getPreparedQueryForDt(payload, 'time_from', 'time_to')
		return new Promise((resolve, reject) => {
			flags.isLoading = true

			notificationsDt.getNotificationsUnreadCount(selectedCompanyId.value, preparedQuery)
				.then((res) => {
					notificationsUnreadCount.value = res
					flags.isLoading = false

					resolve(res)
				})
				.catch(() => {
					flags.isLoading = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading notifications'),
					})

					reject()
				})
		})
	}

	/**
	 * Общий эндпоинт для двух разных bulk-действий (пометить все / пометить выбранные прочитанными),
	 * провайдер его не покрывает.
	 */
	const updateNotificationsMarkAllRead = (body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true

			notificationsDt.patchNotificationsMarkAllRead(selectedCompanyId.value, body)
				.then((res) => {
					flags.isUpdating = false

					resolve(res)
				})
				.catch(() => {
					flags.isUpdating = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error updating notification mark all read'),
					})

					reject()
				})
		})
	}

	const updateNotificationsMarkReadStatus = (body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true

			notificationsDt.patchNotificationsMarkReadStatus(selectedCompanyId.value, body)
				.then((res) => {
					flags.isUpdating = false

					resolve(res)
				})
				.catch(() => {
					flags.isUpdating = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Loading error'),
					})

					reject()
				})
		})
	}

	/**
	 * Настройки уведомлений — отдельный вложенный ресурс, провайдер его не покрывает.
	 */
	const getNotificationsSettings = () => {
		return new Promise((resolve, reject) => {
			flags.isLoading = true

			notificationsDt.getNotificationsSettings(selectedCompanyId.value)
				.then((res) => {
					resolve(res)
				})
				.catch(() => {
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading notifications settings'),
					})

					reject()
				})
				.finally(() => {
					flags.isLoading = false
				})
		})
	}

	const updateNotificationsSettings = (notificationSettingId, body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true

			notificationsDt.patchNotificationsSettings(selectedCompanyId.value, notificationSettingId, body)
				.then((res) => {
					resolve(res)
				})
				.catch(() => {
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error updating notifications settings'),
					})

					reject()
				})
				.finally(() => {
					flags.isUpdating = false
				})
		})
	}

	return {
		flags,
		notifications,
		notification,
		notificationsUnreadCount,
		notificationPreview,

		getNotifications,
		getNotificationsUnreadCount,
		getNotification,
		getNotificationPreview,
		updateNotifications,
		updateNotificationsMarkAllRead,
		updateNotificationsMarkReadStatus,
		getNotificationsSettings,
		updateNotificationsSettings,
	}
}
